import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
// @mui
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  useTheme,
  Grid,
  Link,
} from '@mui/material';
import {
  CheckCircle,
  VideoCall,
  Info,
  EventBusy,
} from '@mui/icons-material';
// hooks
import useMeetingScheduler from '../../hooks/useMeetingScheduler';
// components
import MeetingCalendar from './MeetingCalendar';
import MeetingTimeSlots from './MeetingTimeSlots';
import MeetingTimezoneSelector, { TIMEZONES } from './MeetingTimezoneSelector';

export default function MeetingScheduler({
  calendarId,
  calendarCode,
  user,
  onComplete,
  title = '미팅 예약하기',
  description = '미팅 하실 날짜와 시간을 선택해주세요.',
  additionalQuestions = '',
  duration = 40,
  showTimezone = true,
}) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const {
    loading,
    error,
    availableDates,
    selectedDate,
    selectedTime,
    userTimezone,
    calendarInfo,
    setSelectedDate,
    setSelectedTime,
    setUserTimezone,
    getTimesForDate,
    scheduleMeeting,
  } = useMeetingScheduler(calendarId, calendarCode);

  const [activeStep, setActiveStep] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduledMeeting, setScheduledMeeting] = useState(null);
  const [selectedTimeDisplay, setSelectedTimeDisplay] = useState(null);
  const [selectedDateDay, setSelectedDateDay] = useState(null);
  const [selectedOriginalDate, setSelectedOriginalDate] = useState(null);
  const [selectedOriginalTime, setSelectedOriginalTime] = useState(null);
  const [isWeekendOrHoliday, setIsWeekendOrHoliday] = useState(false);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);

    // 선택한 날짜의 요일 저장
    const dateObj = parseISO(date);
    const dayOfWeek = format(dateObj, 'EEEE', { locale: ko }).replace('요일', '');
    setSelectedDateDay(dayOfWeek);

    setActiveStep(1);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);

    // 선택된 시간의 표시용 정보 찾기
    const timesForDate = getTimesForDate(selectedDate);
    const timeSlot = timesForDate.find(slot => slot.originalTime === time);
    if (timeSlot) {
      setSelectedTimeDisplay(timeSlot.localTime);
      // 원본 날짜와 시간 저장 (KST 기준)
      setSelectedOriginalDate(timeSlot.originalDate || selectedDate);
      setSelectedOriginalTime(timeSlot.originalTime);
      setIsWeekendOrHoliday(timeSlot.isWeekendOrHoliday || false);
    }

    setActiveStep(2);
    setConfirmDialog(true);
  };

  const handleSchedule = async () => {
    setScheduling(true);

    try {
      const additionalData = {
        duration_minutes: duration,
        company_user_phone: user?.phone || '',
        // 원본 날짜와 시간 전달 (KST 기준)
        originalDate: selectedOriginalDate,
        originalTime: selectedOriginalTime,
      };

      // 추가 질문 데이터 처리
      if (additionalQuestions) {
        additionalData.questions = additionalQuestions;
      }

      const result = await scheduleMeeting(additionalData);

      setScheduledMeeting(result);
      enqueueSnackbar('미팅이 성공적으로 예약되었습니다!', { variant: 'success' });

      if (onComplete) {
        onComplete(result);
      }

      setConfirmDialog(false);
      setActiveStep(3);

    } catch (err) {
      enqueueSnackbar(err.message || '미팅 예약에 실패했습니다.', { variant: 'error' });
    } finally {
      setScheduling(false);
    }
  };

  // 예약 완료 화면
  if (activeStep === 3 && scheduledMeeting) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          미팅이 예약되었습니다!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          예약 확인 알림이 발송되었습니다.
        </Typography>

        {scheduledMeeting.zoom_meeting_url && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: theme.palette.grey[50],
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VideoCall />
              <Typography variant="subtitle2" fontWeight={600}>
                Zoom 미팅 정보
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {scheduledMeeting.zoom_meeting_url}
            </Typography>
            {scheduledMeeting.zoom_meeting_password && (
              <Typography variant="caption" color="text.secondary">
                비밀번호: {scheduledMeeting.zoom_meeting_password}
              </Typography>
            )}
          </Paper>
        )}
      </Paper>
    );
  }

  const hasNoAvailableDates = !loading && availableDates.length === 0;

  return (
    <Box>
      {/* 헤더 */}
      {(title || description) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        </Box>
      )}

      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 타임존 선택 */}
      {showTimezone && !hasNoAvailableDates && (
        <Box sx={{ mb: 3 }}>
          <MeetingTimezoneSelector
            value={userTimezone}
            onChange={setUserTimezone}
          />
        </Box>
      )}

      {/* 메인 컨텐츠 */}
      <Box sx={{ position: 'relative' }}>
        <Grid container spacing={3}>
          {/* 달력 */}
          <Grid item xs={12} md={6}>
            <MeetingCalendar
              availableDates={availableDates}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
            />
          </Grid>

          {/* 시간 선택 */}
          <Grid item xs={12} md={6}>
            <MeetingTimeSlots
              date={selectedDate}
              timeSlots={getTimesForDate(selectedDate)}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              userTimezone={userTimezone}
            />
          </Grid>
        </Grid>

        {/* 선택 가능한 날짜가 없을 때 오버레이 */}
        {hasNoAvailableDates && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <EventBusy sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                예약 가능한 시간이 없습니다
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                다른 미팅 시간 요청하기를 누르시거나
                <br />
                <Link
                  href="http://pf.kakao.com/_xbEWUs/chat"
                  target="_blank"
                  underline="hover"
                  sx={{ fontWeight: 600 }}
                >
                  카카오톡 채널로 문의
                </Link>
                해 주세요.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* 로딩 상태 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* 예약 확인 다이얼로그 */}
      <Dialog 
        open={confirmDialog} 
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {calendarInfo?.meetingName} 미팅 예약
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" gutterBottom>
                선택하신 일정
              </Typography>
              <Typography variant="subtitle1">
                {selectedDate && selectedTimeDisplay && (
                  <>
                    {selectedDate} ({selectedDateDay})
                    {' '}{selectedTimeDisplay}
                  </>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {TIMEZONES.find(v => v.value == userTimezone)?.label || '서울'} 시간대 기준
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" gutterBottom>
                연락처
              </Typography>
              <Typography variant="body1">
                {user?.phone || '연락처 정보 없음'}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary" gutterBottom>
                이메일
              </Typography>
              <Typography variant="body1">
                {user?.email || '이메일 정보 없음'}
              </Typography>
            </Box>

            <Box sx={{
              p: 2,
              bgcolor: theme.palette.grey[50],
              borderRadius: 1,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Info sx={{ fontSize: 18, color: theme.palette.info.main }} />
                <Typography variant="subtitle2">
                  예약 안내
                </Typography>
              </Box>
              <ul style={{ marginLeft: "12px", marginBottom: "8px", color: theme.palette.text.secondary }}>
                <li>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    미팅 시간은 {duration}분입니다
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    예약 확인 알림과 당일 리마인드 알림이 전송됩니다
                  </Typography>
                </li>
                {isWeekendOrHoliday && (
                <li>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    상대 측 노쇼나 미팅 링크 오류 시 카카오톡 채널로 문의를 남겨주시면, 미팅 일정 재조율을 도와드리겠습니다
                  </Typography>
                </li>
                )}
                {isWeekendOrHoliday && (
                <li>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    한국 기준 주말 및 공휴일은 운영팀 미운영 시간으로, 즉각적인 대응이 어려울 수 있습니다
                  </Typography>
                </li>
                )}
              </ul>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            size="large"
            onClick={() => setConfirmDialog(false)}
            disabled={scheduling}
          >
            취소
          </Button>
          <Button
            size="large"
            variant="contained" 
            onClick={handleSchedule}
            disabled={scheduling}
            sx={{
              bgcolor: theme.palette.common.black,
              '&:hover': {
                bgcolor: theme.palette.grey[900],
              },
            }}
          >
            {scheduling ? <CircularProgress size={24} /> : '예약하기'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}